import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import { useState, useCallback, useEffect, type ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import {
  setTemporaryLocation,
  setTentativeETALocation,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import type { LocationSelectionMethod } from '../../../../gainsightTrackings/airAmbulanceTrackings';
import { needsWebMercatorConversion } from '../../../../utils/needsWebMercatorConversion';
import usePermissionedGeocoder from '../../../JurisdictionView/AviveAED/usePermissionedGeocoder';
import type { Coordinates, SpatialObject } from '../../types';
import type { GeocodedAddress } from '../components/SuggestedAddress';
import {
  PICKUP_COORDINATES,
  PICKUP_LOCATION,
  LANDING_ZONE_EXTERNAL_ID,
} from '../constants';
import { isValidCoordinates, formatAddressFromObject } from '../utils';
import { formatCoordinates, parseCoordinates } from '../validation';
import useDebouncedAddressUpdate from './useDebouncedAddressUpdate';
import useFormReverseGeocode from './useFormReverseGeocode';
import useLandingZonesLayer from './useLandingZonesLayer';

/**
 * Minimal interface for setting form field values imperatively.
 * Compatible with Formik's FormikProps without coupling to specific form types.
 */
export interface FormFieldRef {
  setFieldValue(field: string, value: unknown, shouldValidate?: boolean): void;
  setFieldTouched(
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ): void;
}

interface UseLocationEditorOptions {
  formRef: { readonly current: FormFieldRef | null };
  initialCoordinates: string;
  initialAddressValid?: boolean;
  initialLandingZone?: SpatialObject | null;
  onLocationSelect?: (
    coords: Coordinates,
    method: LocationSelectionMethod,
  ) => void;
}

export function useLocationEditor({
  formRef,
  initialCoordinates,
  initialAddressValid = true,
  initialLandingZone = null,
  onLocationSelect,
}: UseLocationEditorOptions) {
  const dispatch = useDispatch();

  const [addressToGeocode, setAddressToGeocode] = useState('');
  const [coordsToReverseGeocode, setCoordsToReverseGeocode] =
    useState<Coordinates | null>(null);
  const [isAddressSelected, setIsAddressSelected] =
    useState(initialAddressValid);
  const [isAddressValid, setIsAddressValid] = useState(initialAddressValid);
  const [reverseGeocodeResults, setReverseGeocodeResults] = useState<
    GeocodedAddress[] | null
  >(null);
  const [isUpdatingFromCoords, setIsUpdatingFromCoords] = useState(false);
  const [isUpdatingFromAddress, setIsUpdatingFromAddress] = useState(false);
  const [selectedLandingZone, setSelectedLandingZone] =
    useState<SpatialObject | null>(initialLandingZone);

  const service = useWhichReverseGeocodeService();
  const geocodedResults = usePermissionedGeocoder(
    addressToGeocode,
  ) as GeocodedAddress[];
  const { isReverseGeocoding, foundAddress } = useFormReverseGeocode({
    coordinates: coordsToReverseGeocode,
    service,
  });
  const debouncedSetAddress = useDebouncedAddressUpdate(setAddressToGeocode);
  const { isEnabled, toggleLandingZoneLayer } = useLandingZonesLayer();

  useEffect(() => {
    if (!isEnabled) {
      toggleLandingZoneLayer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const coords = parseCoordinates(initialCoordinates);
    if (coords) {
      dispatch(setTemporaryLocation(coords));
      dispatch(setTentativeETALocation(coords));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setTemporaryLocation(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (coordsToReverseGeocode && isAddressValid) {
      dispatch(setTemporaryLocation(coordsToReverseGeocode));
      dispatch(setTentativeETALocation(coordsToReverseGeocode));
    } else {
      dispatch(setTemporaryLocation(null));
      dispatch(setTentativeETALocation(null));
    }
  }, [dispatch, coordsToReverseGeocode, isAddressValid]);

  const handleReverseGeocodingResults = useCallback(() => {
    if (!formRef.current || !foundAddress || isUpdatingFromAddress) return;
    const { setFieldValue } = formRef.current;

    const { address, addressObject } = foundAddress;
    if (!address && !addressObject) return;

    const formattedAddress = addressObject
      ? formatAddressFromObject(addressObject)
      : address;

    setFieldValue(PICKUP_LOCATION, formattedAddress);
    setReverseGeocodeResults([
      {
        address: formattedAddress,
        location: {
          y: coordsToReverseGeocode?.latitude,
          x: coordsToReverseGeocode?.longitude,
        },
      },
    ]);

    setIsUpdatingFromCoords(false);
  }, [foundAddress, isUpdatingFromAddress, coordsToReverseGeocode, formRef]);

  useEffect(() => {
    if (foundAddress) {
      handleReverseGeocodingResults();
    }
  }, [foundAddress, handleReverseGeocodingResults]);

  const handleSelectSuggestion = useCallback(
    (suggestion: GeocodedAddress): void => {
      if (!formRef.current) return;
      const { setFieldValue, setFieldTouched } = formRef.current;

      setIsUpdatingFromAddress(true);
      const { x, y } = suggestion.location || {};

      let lat = y;
      let lng = x;

      if (
        typeof x === 'number' &&
        typeof y === 'number' &&
        needsWebMercatorConversion(x, y)
      ) {
        [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
      }

      const coordinates = formatCoordinates(lat as number, lng as number);

      setFieldValue(PICKUP_LOCATION, suggestion.address);
      setFieldValue(PICKUP_COORDINATES, coordinates);
      setFieldTouched(PICKUP_LOCATION, true);
      setFieldTouched(PICKUP_COORDINATES, true);

      setIsAddressValid(true);
      setIsAddressSelected(true);
      setReverseGeocodeResults(null);
      setAddressToGeocode('');
      setIsUpdatingFromAddress(false);

      const coords = {
        latitude: lat as number,
        longitude: lng as number,
      };
      setCoordsToReverseGeocode(coords);
      onLocationSelect?.(coords, 'address_input');
    },
    [formRef, onLocationSelect],
  );

  const handleCoordinateChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement> | { target: { value: string } },
      handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
      selectionMethod: LocationSelectionMethod = 'coordinates_input',
    ): void => {
      const { value } = e.target;
      handleChange(e as ChangeEvent<HTMLInputElement>);
      setAddressToGeocode('');

      if (value && selectedLandingZone) {
        setSelectedLandingZone(null);
      }

      const coords = parseCoordinates(value);

      if (coords) {
        const { latitude, longitude } = coords;
        if (isValidCoordinates(latitude, longitude)) {
          setIsUpdatingFromCoords(true);
          setIsAddressValid(true);
          setIsAddressSelected(true);
          setCoordsToReverseGeocode(coords);
          onLocationSelect?.(coords, selectionMethod);
        }
      } else if (!value) {
        setIsAddressValid(false);
        setIsAddressSelected(false);
      }
    },
    [selectedLandingZone, onLocationSelect],
  );

  const handleAddressChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement>,
      handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
    ): void => {
      const { value } = e.target;
      handleChange(e);

      setIsUpdatingFromCoords(false);

      if (value && selectedLandingZone) {
        setSelectedLandingZone(null);
      }

      if (value && value.length > 3) {
        debouncedSetAddress(value);
      } else {
        setReverseGeocodeResults(null);
        setAddressToGeocode('');
      }
    },
    [debouncedSetAddress, selectedLandingZone],
  );

  const handleLandingZoneSelect = useCallback(
    (
      selectedOption: SpatialObject | null,
      setFieldValue: (field: string, value: unknown) => void,
      handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
    ): void => {
      const coordinates = selectedOption?.center?.coordinates;
      if (selectedOption && Array.isArray(coordinates)) {
        const [longitude, latitude] = coordinates;
        const latLng = `${latitude}, ${longitude}`;

        setFieldValue(PICKUP_LOCATION, '');
        setFieldValue(PICKUP_COORDINATES, latLng);
        setFieldValue(
          LANDING_ZONE_EXTERNAL_ID,
          selectedOption?.external_id || '',
        );

        handleCoordinateChange({ target: { value: latLng } }, handleChange);

        setIsAddressValid(true);
        setIsAddressSelected(true);
        setSelectedLandingZone(selectedOption);
      } else {
        setSelectedLandingZone(null);
        setFieldValue(LANDING_ZONE_EXTERNAL_ID, '');
      }
    },
    [handleCoordinateChange],
  );

  const handleGeocodingResults = useCallback(
    (values: { pickup_location: string }): void => {
      if (!formRef.current || !addressToGeocode || isUpdatingFromCoords) return;
      const { setFieldValue } = formRef.current;

      if (!geocodedResults?.length) {
        setIsAddressValid(false);
      } else {
        const exactMatch = geocodedResults.find(
          result =>
            result.address?.toLowerCase() ===
            values.pickup_location?.toLowerCase(),
        );

        setIsAddressValid(true);
        if (exactMatch) {
          setIsUpdatingFromAddress(true);
          const coordinates = formatCoordinates(
            exactMatch.location?.y,
            exactMatch.location?.x,
          );
          setFieldValue(PICKUP_COORDINATES, coordinates);
          setIsUpdatingFromAddress(false);
        }
      }
    },
    [addressToGeocode, geocodedResults, isUpdatingFromCoords, formRef],
  );

  return {
    isReverseGeocoding,
    isAddressValid,
    isAddressSelected,
    geocodedResults,
    reverseGeocodeResults: reverseGeocodeResults || undefined,
    selectedLandingZone,
    setAddressToGeocode,
    setIsAddressValid,
    setIsAddressSelected,
    handleSelectSuggestion,
    handleCoordinateChange,
    handleAddressChange,
    handleLandingZoneSelect,
    handleGeocodingResults,
    triggerReverseGeocode: setCoordsToReverseGeocode,
  };
}
